import React from 'react';
import { Pencil, Plus, Trash2, Waypoints } from 'lucide-react';
import { Button } from '../../ui/Button/Button';
import { ChannelTile } from '../ChannelTile';
import { EmptyNote } from '../ui/EmptyNote';
import { PanelSection } from '../ui/PanelSection';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { BridgeSheet } from '../sheets/BridgeSheet';
import { RailSheet } from '../sheets/RailSheet';
import { TargetSheet } from '../sheets/TargetSheet';
import { LoadSheet } from '../sheets/LoadSheet';
import { ACCENT_SPECS } from '../../../lib/rcu/catalog/accent';
import { DALI_BRIDGE_ADDRESS, daliBridgeLabel } from '../../../lib/rcu/catalog/dali';
import { RCU_LIMITS } from '../../../lib/rcu/catalog/limits';
import { RAIL_TYPE_SPECS } from '../../../lib/rcu/catalog/railType';
import type { DaliBridge, DinRail, WiredLoad } from '../../../lib/rcu/model';
import { channelsOf } from '../../../lib/rcu/query/targets';
import type { ChannelTarget } from '../../../lib/rcu/query/targets';
import { dinTarget } from '../../../lib/rcu/query/targets';
import { moduleCount } from '../../../lib/rcu/query/capacity';
import { loadsOnRail, sortedDaliBridges } from '../../../lib/rcu/query/lookup';
import { railTypeUsages, sortedRails } from '../../../lib/rcu/query/railUsage';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useCommandFeedback } from '../useCommandFeedback';

const RailCard: React.FC<{
  rail: DinRail;
  onOpenTarget: (target: ChannelTarget) => void;
  onEdit: () => void;
  onRemove: () => void;
}> = ({ rail, onOpenTarget, onEdit, onRemove }) => {
  const config = useRcuConfigStore((state) => state.config);
  const spec = RAIL_TYPE_SPECS[rail.type];
  const accent = ACCENT_SPECS[spec.accent];
  const wired = loadsOnRail(config, rail.id).length;

  return (
    <div className={`rounded-2xl border bg-white p-4 ${accent.border}`}>
      <header className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-medium text-[#333333]">
            <span className={`h-2.5 w-2.5 rounded-full ${accent.dot}`} />
            <span className="truncate">{spec.label}</span>
          </p>
          <p className="mt-0.5 text-xs text-[#666666]">
            Address {rail.address} · {wired}/{spec.channels} channels wired
            {rail.label.trim() === '' ? '' : ` · ${rail.label}`}
          </p>
        </div>
        <div className="flex shrink-0 items-center">
          <button
            type="button"
            aria-label="Edit rail"
            onClick={onEdit}
            className="flex h-9 w-9 items-center justify-center rounded-full text-[#666666] transition-colors hover:bg-[#F5F5F5] hover:text-[#D4A054]"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Remove rail"
            onClick={onRemove}
            className="flex h-9 w-9 items-center justify-center rounded-full text-[#666666] transition-colors hover:bg-[#F5F5F5] hover:text-[#F44336]"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </header>

      <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {channelsOf(rail).map((channel) => (
          <li key={channel}>
            <ChannelTile target={dinTarget(config, rail, channel)} onSelect={onOpenTarget} />
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * One bridge row. Presentational, so a render test can check what a technician
 * sees without standing up the store.
 */
export const DaliBridgeCard: React.FC<{
  bridge: DaliBridge;
  onEdit: () => void;
  onRemove: () => void;
}> = ({ bridge, onEdit, onRemove }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#E0E0E0] bg-white px-4 py-3">
    <div className="min-w-0">
      <p className="flex items-center gap-2 text-sm font-medium text-[#333333]">
        <Waypoints className="h-4 w-4 text-[#D4A054]" />
        {bridge.label.trim() === '' ? daliBridgeLabel(bridge.address, bridge.master) : bridge.label}
      </p>
      <p className="mt-0.5 text-xs text-[#666666]">
        Module address {bridge.address} · DALI bus {bridge.master} · no guest-facing channels
      </p>
    </div>
    <div className="flex shrink-0 items-center">
      <button
        type="button"
        aria-label="Edit DALI bridge"
        onClick={onEdit}
        className="flex h-9 w-9 items-center justify-center rounded-full text-[#666666] transition-colors hover:bg-[#F5F5F5] hover:text-[#D4A054]"
      >
        <Pencil className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-label="Remove DALI bridge"
        onClick={onRemove}
        className="flex h-9 w-9 items-center justify-center rounded-full text-[#666666] transition-colors hover:bg-[#F5F5F5] hover:text-[#F44336]"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  </div>
);

/** DIN rails fitted to the RCU, and the loads on their channels. */
export const RailsPanel: React.FC = () => {
  const config = useRcuConfigStore((state) => state.config);
  const removeRail = useRcuConfigStore((state) => state.removeRail);
  const removeDaliBridge = useRcuConfigStore((state) => state.removeDaliBridge);
  const report = useCommandFeedback();

  const [railSheet, setRailSheet] = React.useState<{ rail: DinRail | null } | null>(null);
  const [bridgeSheet, setBridgeSheet] = React.useState<{ bridge: DaliBridge | null } | null>(null);
  const [removingBridge, setRemovingBridge] = React.useState<DaliBridge | null>(null);
  const [openTarget, setOpenTarget] = React.useState<ChannelTarget | null>(null);
  const [editing, setEditing] = React.useState<{ load: WiredLoad | null; target: ChannelTarget } | null>(
    null,
  );
  const [removing, setRemoving] = React.useState<DinRail | null>(null);

  const rails = sortedRails(config);
  const usages = railTypeUsages(config);
  const bridges = sortedDaliBridges(config);
  const modules = moduleCount(config);

  return (
    <div className="space-y-4">
      <PanelSection
        title="DIN rails"
        description={`${rails.length} of ${usages.length} rail types fitted · ${modules}/${RCU_LIMITS.MAX_MODULES_TOTAL} modules, bridges included. Addresses are unique per type.`}
        action={
          <Button variant="primary" size="sm" onClick={() => setRailSheet({ rail: null })}>
            <Plus className="mr-2 h-3.5 w-3.5" />
            Add rail
          </Button>
        }
      >
        {rails.length === 0 ? (
          <EmptyNote
            message="No rails fitted yet. Add a rail, then wire its channels."
            action={
              <Button variant="secondary" size="sm" onClick={() => setRailSheet({ rail: null })}>
                Add the first rail
              </Button>
            }
          />
        ) : (
          <div className="flex flex-wrap gap-2">
            {usages.map((usage) => {
              const spec = RAIL_TYPE_SPECS[usage.type];
              const accent = ACCENT_SPECS[spec.accent];
              return (
                <span
                  key={usage.type}
                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium ${accent.tile} ${accent.text}`}
                >
                  <span className={`h-2 w-2 rounded-full ${accent.dot}`} />
                  {spec.shortLabel} {usage.fitted}/{usage.perTypeLimit}
                  {usage.canAdd ? '' : ' · full'}
                </span>
              );
            })}
          </div>
        )}
      </PanelSection>

      {rails.map((rail) => (
        <RailCard
          key={rail.id}
          rail={rail}
          onOpenTarget={setOpenTarget}
          onEdit={() => setRailSheet({ rail })}
          onRemove={() => setRemoving(rail)}
        />
      ))}

      <PanelSection
        title="DALI bridges"
        description={`Bridges are addressed ${DALI_BRIDGE_ADDRESS.MIN}–${DALI_BRIDGE_ADDRESS.MAX} and each owns one DALI bus. They come out of the same module budget as the rails.`}
        action={
          <Button variant="secondary" size="sm" onClick={() => setBridgeSheet({ bridge: null })}>
            <Plus className="mr-2 h-3.5 w-3.5" />
            Add bridge
          </Button>
        }
      >
        {bridges.length === 0 ? (
          <EmptyNote message="No DALI bridges fitted. Add one if the drawing shows a DALI bus on this RCU." />
        ) : (
          <div className="space-y-2">
            {bridges.map((bridge) => (
              <DaliBridgeCard
                key={bridge.id}
                bridge={bridge}
                onEdit={() => setBridgeSheet({ bridge })}
                onRemove={() => setRemovingBridge(bridge)}
              />
            ))}
          </div>
        )}
      </PanelSection>

      {railSheet !== null && (
        <RailSheet isOpen rail={railSheet.rail} onClose={() => setRailSheet(null)} />
      )}

      {bridgeSheet !== null && (
        <BridgeSheet
          isOpen
          bridge={bridgeSheet.bridge}
          onClose={() => setBridgeSheet(null)}
        />
      )}

      <ConfirmDialog
        isOpen={removingBridge !== null}
        title="Remove DALI bridge"
        message={
          removingBridge === null
            ? ''
            : `Removing ${daliBridgeLabel(removingBridge.address, removingBridge.master)} frees its module slot. The fixtures behind it keep their DALI addresses and are driven through whichever bridge replaces it.`
        }
        confirmLabel="Remove bridge"
        destructive
        onCancel={() => setRemovingBridge(null)}
        onConfirm={() => {
          if (removingBridge === null) return;
          report(removeDaliBridge(removingBridge.id), 'DALI bridge removed.');
          setRemovingBridge(null);
        }}
      />

      {openTarget !== null && (
        <TargetSheet
          isOpen
          target={openTarget}
          onClose={() => setOpenTarget(null)}
          onEditLoad={(load, target) => {
            setOpenTarget(null);
            setEditing({ load, target });
          }}
        />
      )}

      {editing !== null && (
        <LoadSheet
          isOpen
          load={editing.load}
          channel={editing.target.ref}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        isOpen={removing !== null}
        title="Remove rail"
        message={
          removing === null
            ? ''
            : `Removing ${RAIL_TYPE_SPECS[removing.type].shortLabel} at address ${removing.address} unwires its ${loadsOnRail(config, removing.id).length} load(s). The loads themselves are kept.`
        }
        confirmLabel="Remove rail"
        destructive
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          if (removing === null) return;
          report(removeRail(removing.id), 'Rail removed.');
          setRemoving(null);
        }}
      />
    </div>
  );
};

export default RailsPanel;
